import styled, { css } from "styled-components";
import { useEffect, useMemo, useRef, useState } from "react";
import { TContent } from ".";
import {
  UilCheckCircle,
  UilDollarSign,
  UilEnter,
  UilImport,
  UilPen,
  UilTrash,
  UilUpload,
} from "@iconscout/react-unicons";
import Dropdown from "../Dropdown";
import Button from "../Button";
import {
  backendErrorMessageHandler,
  handleGetDataFromLocalStorage,
} from "../../../utils/miscellaneous";
import { localStorageConstants } from "../../../utils/constants";
import {
  TInvoice,
  TInvoiceManifestastionStatus,
  setToastState,
} from "../../../redux/reducers/globalState";
import apiService from "../../../services/api";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import { fetchInvoices } from "../../../redux/actions";
import axios, { CancelTokenSource } from "axios";
import SVG from "../../ui/SVG";
import BoletoIcon from "../../../assets/images/boleto.svg";

const sharedMenuStyles = css<{ $shouldshow: "show" | "hide"; $width?: string }>`
  width: ${({ $width }) => $width || "248px"};
  padding: 16px;
  margin: 0;
  position: absolute;
  z-index: ${({ $shouldshow }) => ($shouldshow === "show" ? "999999" : "-1")};
  background: var(--color-light-0);
  border: 1px solid var(--color-dark-blue-300);
  border-radius: 8px;

  opacity: ${({ $shouldshow }) => ($shouldshow === "show" ? 1 : 0)};
  visibility: ${({ $shouldshow }) =>
    $shouldshow === "show" ? "visible" : "hidden"};
  transition: height 0.2s ease-in-out, opacity 0.2s ease-in-out,
    visibility 0.2s ease-in-out, z-index 0.15s ease-in-out;

  & button:nth-child(1),
  & p.optGroup {
    visibility: ${({ $shouldshow }) =>
      $shouldshow === "show" ? "visible" : "hidden"};
    transition: visibility 0.1s ease-in-out;
  }
  & .optGroupContainer {
    position: relative;
    height: 31px;
  }
  & p.optGroup {
    position: absolute;
    z-index: 1;
    top: 25%;
    left: 12px;
    font-family: var(--font-paragraph-default);
    font-size: 12px;
    color: var(--color-dark-blue-400);
  }
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: ${({ $shouldshow }) => ($shouldshow === "show" ? "100%" : "0%")};
    width: 100%;
    background: var(--color-light-0);
    border-radius: 8px;
    /* border removed from ::before, now on container */
    border: none;
    transition: height 0.2s ease-in-out;
  }
`;

const Container = styled.menu<{ $shouldshow: "show" | "hide" }>`
  ${sharedMenuStyles}
  ${({ $shouldshow }) =>
    $shouldshow === "hide" ? "height: 0px;overflow: hidden;" : "height: auto;"}
`;

const ItemButton = styled.button<{
  $label?: string;
  $shouldshow: "show" | "hide";
}>`
  position: relative;

  cursor: pointer;

  width: 100%;
  padding: 8px 0;

  &:not(:last-child) {
    margin-bottom: 8px;
  }

  background: var(--color-light-0);

  border-radius: 8px;

  visibility: ${({ $shouldshow }) =>
    $shouldshow === "show" ? "visible" : "hidden"};
  transition: visibility 0.1s ease-in-out;

  p {
    font-family: var(--font-paragraph-default);
    color: var(--color-text-paragraph);
    font-size: 14px;
    text-align: left;
    margin-left: 8px;

    display: flex;
    align-items: center;

    svg {
      width: 14px;
      height: 14px;
    }
  }

  &:hover {
    background: var(--color-light-200);
  }
`;

export const NextContainer = styled.menu<{ $shouldshow: "show" | "hide" }>`
  ${sharedMenuStyles}

  right: 50px;

  width: 311px;
  & > h2 {
    position: relative;
    font-family: var(--font-paragraph-bold);
    font-size: 14px;
    color: var(--color-text-paragraph);
    margin-bottom: 12px;
  }
  & > p {
    position: relative;
    font-size: 14px;
    margin-bottom: 12px;
    color: #d9534f;
  }
  & > div {
    width: 100%;
  }
  & > div > div > button p.content {
    top: 13px;
  }
`;
export const FilterArea = styled.div`
  position: relative;

  border-top: 1px solid var(--color-light-200);
  margin-top: 12px;

  .buttonsArea {
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: end;

    width: 100%;

    & > *:last-child {
      margin-left: 8px;
    }
  }
`;

export const TextArea = styled.textarea`
  padding-top: 18px;
  padding-left: 12px;

  position: relative;
  height: 67px;
  width: 296px;

  font-size: 14px;
  font-family: var(--font-paragraph-default);
  color: var(--color-text-paragraph);

  border-radius: 8px;
  border: none;

  outline: none;
  resize: none;
`;

export const TextAreaContainer = styled.div<{
  $placeholder: string;
  $justificationText?: string;
}>`
  position: relative;

  cursor: text;

  margin-top: 12px;
  margin-bottom: 30px;

  height: 92px;
  width: 390px;

  border-radius: 8px;
  border: 1px solid var(--color-dark-blue-300);

  box-shadow: 0px 1px 3px 0px #0000001a;

  &::before {
    content: "${({ $placeholder }) => $placeholder}";
    font-size: ${({ $justificationText }) =>
      $justificationText && $justificationText.length > 0 ? "9" : "14"}px;
    font-family: var(--font-paragraph-default);
    color: var(--color-text-labels);

    position: absolute;
    z-index: 1;

    top: ${({ $justificationText }) =>
      $justificationText && $justificationText.length > 0 ? "10" : "18"}px;
    left: 12px;

    transition: top 0.2s ease-in-out, font-size 0.2s ease-in-out;
  }

  &::after {
    content: "${({ $justificationText }) =>
      $justificationText && $justificationText.length} de 256 caracteres";

    position: absolute;
    z-index: 1;
    left: 0px;
    bottom: -15px;

    font-size: 10px;
    font-family: var(--font-paragraph-default);
    color: var(--color-text-labels);
  }

  &:focus-within {
    border: 1px solid var(--color-main-blue);

    &::before,
    .mandatory {
      font-size: 9px;
      top: 10px;
    }

    &::before {
      color: var(--color-main-blue);
    }

    .mandatory {
      left: 175px;
    }
  }

  .mandatory {
    position: absolute;
    ${({ $justificationText }) =>
      $justificationText && $justificationText.length > 0
        ? "font-size: 9px;" + "left: 175px;" + "top: 10px;"
        : "font-size: 14px;" + "left: 266px;" + "top: 18px;"}
    color: var(--color-aux-danger-700);

    transition: top 0.2s ease-in-out, left 0.2s ease-in-out,
      font-size 0.2s ease-in-out;
  }
`;

type TCommonProps = {
  list: "default" | "filter";
  content: TContent[];
  onClick?: (selectedIndex: string, selected: TContent[]) => void;
  shouldShow: "show" | "hide";
  setShouldShow: React.Dispatch<React.SetStateAction<"show" | "hide">>;
  optGroup?: string;
  setshouldShowNextMenu: React.Dispatch<React.SetStateAction<boolean>>;
  shouldShowNextMenu: boolean;
};

export type TManifestationStatusOptions =
  | "Ciência da emissão"
  | "Confirmação da operação"
  | "Desconhecimento da operação"
  | "Operação não realizada";

type TMenuProps = TCommonProps;

const Menu: React.FC<TMenuProps> = ({
  content,
  onClick,
  shouldShow,
  optGroup,
  setShouldShow,
  setshouldShowNextMenu,
  shouldShowNextMenu,
}) => {
  const MANIFESTATION_OPTIONS: TManifestationStatusOptions[] = useMemo(
    () => [
      "Ciência da emissão",
      "Confirmação da operação",
      "Desconhecimento da operação",
      "Operação não realizada",
    ],
    []
  );

  const [selected, setSelected] = useState<TManifestationStatusOptions | undefined>(undefined);

  const dispatch = useTypedDispatch();

  const [justificationText, setJustificationText] = useState<
    string | undefined
  >();

  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const TEXT_AREA_MAX_lENGTH = 256;

  const localSelectInvoice = handleGetDataFromLocalStorage(
    localStorageConstants.SELECT_INVOICE
  ) as TInvoice;

  const handleTextAreaOnInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setTimeout(() => {
      if (
        textAreaRef.current &&
        textAreaRef.current.value.length <= TEXT_AREA_MAX_lENGTH
      ) {
        setJustificationText((e.target as HTMLTextAreaElement).value);
      }
    }, 1000);
  };

  const handleFetchInvoices = () => {
    // Always create a new token for this fetch
    const localToken = axios.CancelToken.source();
    dispatch(
      fetchInvoices({
        cancelToken: localToken.token,
      })
    );
  };

  const isSituationLocked = [2, 3].includes(localSelectInvoice?.situation);

  const getSituationLabel = (situation?: number) => {
    switch (situation) {
      case 1:
        return "Uso autorizado"
      case 2:
        return "Uso denegado";
      case 3:
        return "NF-e Cancelada";
      default:
        return situation;
    }
  };

  const handleDisabledOptions = () => {
    if (!localSelectInvoice?.manifestation) return [];

    let current = -1;

    switch (localSelectInvoice.manifestation) {
      case 10: current = 0; break;
      case 20: current = 1; break;
      case 30: current = 2; break;
      case 40: current = 3; break;
    }

    if (current == 0) return [current];

    return [0, current];
  };

  const handleMenuOptionRender = (value: TContent) => {
    switch (value) {
      case "editStatus":
        return (
          <>
            <UilPen />
            &nbsp;Manifestar nota
          </>
        );
      case "markAsFinished":
        return (
          <>
            <UilCheckCircle />
            &nbsp;Marcar como finalizada
          </>
        );
      case "markAsPending":
        return (
          <>
            <UilEnter />
            &nbsp;Marcar como pendente
          </>
        );
      case "downloadFile":
        return (
          <>
            <UilImport />
            &nbsp;Baixar arquivo
          </>
        );
      case "deletePaymentSlip":
        return (
          <>
            <UilTrash />
            &nbsp;Recusar boleto
          </>
        );
      case "openGoodsModal":
        return (
          <>
            <UilImport />
            &nbsp;Criar entrada de mercadorias
          </>
        );
      case "openPayableModal":
        return (
          <>
            <UilDollarSign />
            &nbsp;Criar/Vincular contas a pagar
          </>
        );
      case "importFile":
        return (
          <>
            <UilUpload />
            &nbsp;Adicionar arquivo
          </>
        );
      case "linkPaymentSlip":
        return (
          <>
            <SVG src={BoletoIcon} /> &nbsp;Vincular boleto
          </>
        );
    }
  };

  const getFirstEnabledIndex = () => {
    return MANIFESTATION_OPTIONS.findIndex((_, index) => !handleDisabledOptions().includes(index));
  };

  useEffect(() => {
    return () => {
      setTimeout(() => {
        if (cancelTokenSource.current) cancelTokenSource.current.cancel();
      }, 500);
    };
  }, []);

  useEffect(() => {
    setSelected(MANIFESTATION_OPTIONS[getFirstEnabledIndex()]);
  }, [localSelectInvoice?.manifestation]);

  return (
    <>
      {!shouldShowNextMenu && (
        <Container $shouldshow={shouldShow}>
          {optGroup && (
            <div className="optGroupContainer">
              <p className="optGroup">{optGroup}</p>
            </div>
          )}
          {content.map((value, index) => {
            return (
              value && (
                <ItemButton
                  $shouldshow={shouldShow}
                  key={index}
                  onClick={() => {
                    if (value === "editStatus") {
                      setshouldShowNextMenu(true);
                      setTimeout(() => {
                        setShouldShow("show");
                      }, 20);
                    }
                    if (onClick) onClick(`${index}`, [value]);
                  }}
                >
                  <p>{handleMenuOptionRender(value)}</p>
                </ItemButton>
              )
            );
          })}
        </Container>
      )}
      {
        /* shouldShowNextMenu && shouldShow === "show"  && */ <NextContainer
          $shouldshow={shouldShowNextMenu ? "show" : "hide"}
        >
          <h2>Manifestar nota</h2>

          <Dropdown
            key={selected + '-' + localSelectInvoice?.situation}
            button={{
              variant: "unlabeled",
              placeholder: "",
              labels: [MANIFESTATION_OPTIONS[getFirstEnabledIndex()]],
              disabled: isSituationLocked,
            }}
            menu={{
              variant: "unlabeled",
              options: MANIFESTATION_OPTIONS,
              search: false,
              forceSelectOptionsByIndex: [getFirstEnabledIndex()],
              disabledOptions: handleDisabledOptions(),
              shouldHideActiveIcon: true,
              onSelect(index) {
                setSelected(index as TManifestationStatusOptions);
              },
            }}
          />

          {isSituationLocked && (
            <p>Não é possível manifestar notas com situação {getSituationLabel(localSelectInvoice?.situation)}</p>
          )}

          {MANIFESTATION_OPTIONS.filter(
            (option) =>
              option.toLowerCase().includes("desconhecimento") ||
              option.toLowerCase().includes("não realizada")
          ).includes(selected as TManifestationStatusOptions) && selected !== undefined && (
            <TextAreaContainer
              $placeholder="Justificativa (mínimo de 15 caracteres) "
              $justificationText={justificationText}
              role="button"
              onClick={() => {
                if (textAreaRef.current !== null) textAreaRef.current.focus();
              }}
            >
              <TextArea
                ref={textAreaRef}
                minLength={15}
                maxLength={TEXT_AREA_MAX_lENGTH}
                onInput={handleTextAreaOnInput}
              />
              <span className="mandatory">&nbsp;*</span>
            </TextAreaContainer>
          )}
          <FilterArea>
            <div className="buttonsArea">
              <Button
                variant="secondary"
                icon="noIcon"
                text="Cancelar"
                onClick={() => {
                  setShouldShow("hide");
                  setshouldShowNextMenu(false);
                }}
              />
              <Button
                disabled={
                  isSituationLocked ||
                  (
                    MANIFESTATION_OPTIONS.filter(
                      (option) =>
                        option.toLowerCase().includes("desconhecimento") ||
                        option.toLowerCase().includes("não realizada")
                    ).includes(selected as TManifestationStatusOptions) && selected !== undefined &&
                    (justificationText === undefined || justificationText.length < 15)
                  )
                }
                variant="primary"
                icon="checkmark"
                iconPosition="left"
                text="Salvar"
                onClick={() => {
                  if (localSelectInvoice) {
                    let result;
                    switch (selected) {
                      case MANIFESTATION_OPTIONS[0]:
                        result = 10;
                        break;
                      case MANIFESTATION_OPTIONS[1]:
                        result = 20;
                        break;
                      case MANIFESTATION_OPTIONS[2]:
                        result = 30;
                        break;
                      case MANIFESTATION_OPTIONS[3]:
                        result = 40;
                        break;
                    }

                    cancelTokenSource.current = axios.CancelToken.source();

                    apiService
                      .updateInvoiceManifestation(
                        localSelectInvoice.uuid,
                        result as TInvoiceManifestastionStatus,
                        cancelTokenSource.current.token,
                        justificationText
                      )
                      .then(() => {
                        dispatch(
                          setToastState({
                            variant: "success",
                            shouldShow: true,
                            message: {
                              title: "Nota manifestada",
                              description: `A nota nº ${localSelectInvoice.number} foi manifestada como ${selected}`,
                            },
                          })
                        );
                        handleFetchInvoices();
                      })
                      .catch((e: any) => {
                        console.error("error: ", e);

                        const errorMessage = backendErrorMessageHandler(e);

                        dispatch(
                          setToastState({
                            variant: "error",
                            shouldShow: true,
                            message: {
                              title: "Erro ao manifestar nota",
                              description:
                                errorMessage ??
                                "Ocorreu um erro ao fazer manifestação, tente novamente",
                            },
                          })
                        );
                      })
                      .finally(() => {
                        setSelected(MANIFESTATION_OPTIONS[0]);
                      });
                  }

                  setShouldShow("hide");
                  setshouldShowNextMenu(false);
                }}
              />
            </div>
          </FilterArea>
        </NextContainer>
      }
    </>
  );
};

export default Menu;
